function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = [],
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`,
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open"),
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open"),
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*",
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this),
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min),
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function setLocalStorageItem(key, data) {
  try {
    return localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    // Something went wrong
    return null;
  }
}

function getLocalStorageItem(key) {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    // Something went wrong
    return null;
  }
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options,
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid,
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this),
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

Shopify.formatMoney = function (cents, format) {
  let defaultFormat = window["defaultFormat"] || "0";
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }

  var value = "";
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var separatorSample = window["defaultFormatWithSeparator"] || "10,000.00";

  var formatString = format;

  // If there's no format provided
  if (!formatString) {
    // parse the number in the separatorSample (not using placeholderRegex), and find the separator
    // for example (€10.000,00 => '.' as separator) or '$10,000.00' => ',' as separator) or ('10 000 kr' => ' ' as separator)
    let match = separatorSample.match(
      /(\D*)\d{1,3}(\D*)\d{1,3}(\D*)\d{1,3}(\D*)/,
    );
    let separator = match[2];

    switch (separator) {
      case " ":
        formatString = defaultFormat.replace(
          0,
          "{{ amount_no_decimals_with_space_separator }}",
        );
        break;
      case ",":
        formatString = defaultFormat.replace(
          0,
          "{{ amount_no_decimals_with_comma_separator }}",
        );
        break;
      default:
        formatString = defaultFormat.replace(0, "{{ amount_no_decimals }}");
        break;
    }
  }

  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split("."),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
      cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ",", ".");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

// In your global.js or a dedicated file
class CurrencyConvert extends HTMLElement {
  constructor() {
    super();
    this.render();
  }

  render() {
    // 1) Get the raw cent amount from the attribute
    let amountInCents = parseInt(this.getAttribute("data-amount"), 10) || 0;

    // 2) Grab the current rate from the global object
    //    (Adjust property names as needed)
    let rate = parseFloat(window.Shopify.currency?.rate) || 1;

    // 3) Multiply cents by the rate
    //    NOTE: if your rate is for entire currency units, you might need to
    //    parse carefully. Typically "rate" means e.g. 1.05 for 5% difference, etc.
    let convertedCents = amountInCents * rate;

    // 4) Format the final value
    //    e.g. Shopify.formatMoney expects integer cents
    //    If `convertedCents` is a float, you may want to round it.
    let formatted = Shopify.formatMoney(Math.round(convertedCents));

    // 5) Update the custom element content
    this.textContent = formatted;
  }
}

// Finally, define the custom element
customElements.define("currency-convert", CurrencyConvert);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)",
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this)),
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary"),
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button"),
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus,
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`,
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus,
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`,
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary"),
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom",
      )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset,
      )}px`,
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset,
        )}px`,
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`,
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false),
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model") &&
          event.target.name !== "previous-slide" &&
          event.target.name !== "next-slide"
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
    if (typeof this.onShow === "function") {
      this.onShow();
    }
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
    if (typeof this.onHide === "function") {
      this.onHide();
    }
  }
}
customElements.define("modal-dialog", ModalDialog);

class NotifyMeModal extends ModalDialog {
  constructor() {
    super();
    this.form = this.querySelector(".notify-modal-form");
    this.formButton = this.querySelector(
      '.notify-modal-form button[type="submit"]',
    );
    this.spinner = this.formButton.querySelector(".loading__spinner");
    this.notifyMeContent = this.querySelector("#notifyMeContent");
    this.successContent = this.querySelector("#notifyMeSuccess");
    this.form.addEventListener("submit", this.onFormSubmit.bind(this));
  }
  onShow() {
    let eyePower = getLocalStorageItem("p");
    if (eyePower) {
      let [firstPower] = eyePower.split(",");
      if (firstPower) {
        let variantSelect = this.querySelector("select");
        if (variantSelect) {
          let eyePowerOption = [].find.call(
            variantSelect.options,
            (o) => o.label === firstPower,
          );
          if (eyePowerOption) {
            variantSelect.value = eyePowerOption.value;
          }
        }
      }
    }
  }

  onHide() {
    this.successContent.style.display = "none";
    this.notifyMeContent.style.display = "block";
  }

  onFormSubmit(e) {
    e.preventDefault();
    this.formButton.classList.add("loading");
    this.spinner.classList.remove("hidden");
    let email = this.form.elements["notifyMeEmail"]["value"];
    let klaviyoId = this.form.elements["klaviyoId"]["value"];
    let variantId = this.form.elements["variant"]["value"];
    let isPushAllowed = this.form.elements["notifyMePush"]["checked"];
    if (email) {
      fetch(
        "https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=" +
          klaviyoId,
        {
          method: "POST",
          headers: {
            revision: "2024-02-15",
            accept: "application/json",
            "content-type": "application/json",
            "x-klaviyo-onsite": "1",
          },
          body: JSON.stringify({
            data: {
              type: "back-in-stock-subscription",
              attributes: {
                channels: ["EMAIL"],
                profile: {
                  data: {
                    type: "profile",
                    attributes: {
                      email: email,
                    },
                  },
                },
              },
              relationships: {
                variant: {
                  data: {
                    type: "catalog-variant",
                    id: "$shopify:::$default:::" + variantId,
                  },
                },
              },
            },
          }),
        },
      )
        .then((data) => {
          this.formButton.classList.remove("loading");
          this.spinner.classList.add("hidden");
          this.successContent.style.display = "block";
          this.notifyMeContent.style.display = "none";
          let pushowl = window["pushowl"];
          if (
            isPushAllowed &&
            pushowl &&
            typeof pushowl["registerForEvent"] === "function"
          ) {
            pushowl["registerForEvent"]("modal-dialog ", false);
          }
          console.log("Successfully subscribed to back in stock");
          // Handle the response data here
        })
        .catch((error) => {
          this.formButton.classList.remove("loading");
          this.spinner.classList.add("hidden");
          // Handle any errors here
          console.error("Failed to subscribe to back in stock", error);
        });
    }
  }
}
customElements.define("notify-me-modal", NotifyMeModal);

class HighlightedContent extends HTMLElement {
  constructor() {
    super();
    this.init();
    this.externalStyle = null; // Store reference to the injected style
  }

  init() {
    // Grab the “content” container
    this.contentEl = this.querySelector(".highlighted-content__content");
    if (!this.contentEl) return;

    // Grab the modal
    this.modalEl = document.getElementById(this.dataset.modalId);
    if (!this.modalEl) return;

    // The element in which we will inject fetched content
    this.popupContentEl = this.modalEl.querySelector(".page_content");

    // A simple loader placeholder
    this.loader = this.modalEl.querySelector(".loading__spinner");

    if (this.loader) {
      this.loader.classList.remove("hidden");
      this.popupContentEl.innerHTML = "";
    }

    // For each link, intercept clicks
    const pageLinks = this.contentEl.querySelectorAll("a");
    pageLinks.forEach((link) => {
      link.addEventListener("click", (evt) => {
        evt.preventDefault();

        // Show modal
        this.showModal();

        // Append “view=modal-content” to the link’s URL
        const url = new URL(link.href, window.location.origin);

        if (url.hostname === window.location.hostname) {
          // Shopify internal page: Fetch content
          url.searchParams.set("view", "modal-content");

          // Fetch the page content
          this.fetchPage(url).catch((err) => {
            console.error("Error fetching page:", err);
            this.popupContentEl.innerHTML = `<p>Could not load content.</p>`;
          });
        } else {
          // External page: Embed in iframe
          this.loadExternalPage(link.href);
        }
      });
    });

    // Hook into the modal's onHide so we can clear out content each time
    // You can also do this by listening for a custom event, but we'll just use a property:
    this.modalEl.onHide = () => {
      // Clear out the content and show loader next time
      this.popupContentEl.innerHTML = "";
      this.modalEl.classList.remove("external-embed"); // Remove the external style when modal is hidden
    };
  }

  /**
   * Show the modal and display a loader while fetching
   */
  showModal() {
    if (this.loader) {
      this.loader.classList.remove("hidden");
    }
    // Show the loader before content arrives
    if (this.popupContentEl) {
      this.popupContentEl.innerHTML = "";
    }
    // Use the modal’s `.show()` method from your existing ModalDialog class
    this.modalEl.show();
  }

  /**
   * Fetch the page content at `url` and inject into modal
   */
  async fetchPage(url) {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const html = await response.text();

    if (this.loader) {
      this.loader.classList.add("hidden");
    }
    if (this.popupContentEl) {
      this.popupContentEl.innerHTML = html;
    }
  }

  // Function for loading external sites inside an iframe
  loadExternalPage(url) {
    const urlObj = new URL(url);

    if (!urlObj.hostname.includes("typeform.com")) {
      console.error("Blocked embedding of untrusted external URL:", url);
      this.popupContentEl.innerHTML = `<p>Embedding this page is not allowed.</p>`;
      return;
    }

    if (this.loader) {
      this.loader.classList.add("hidden");
    }

    // Create and inject CSS
    this.modalEl.classList.add("external-embed");
    this.popupContentEl.innerHTML = `
      <div class="external-modal">
        <iframe src="${url}" class='form' style="width: 100%; height: 100dvh; border: none; margin: 0;"></iframe>
      </div>
    `;
  }
}

// Register the custom element
customElements.define("highlighted-content", HighlightedContent);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container",
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`,
      ),
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
    if (this.dataset.autoPlayVideo === "true") {
      this.playVideo();
    }
  }

  playVideo(focus) {
    const content = document.createElement("div");
    content.appendChild(
      this.querySelector("template").content.firstElementChild.cloneNode(true),
    );

    this.setAttribute("loaded", true);
    const deferredElement = this.appendChild(
      content.querySelector("video, model-viewer, iframe"),
    );
    if (focus) deferredElement.focus();
    if (
      deferredElement.nodeName == "VIDEO" &&
      deferredElement.getAttribute("autoplay")
    ) {
      // force autoplay for safari
      deferredElement.play();
    }
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      this.playVideo(focus);
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver(
      debounce((entries) => this.initPages(), 100),
    );
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  gerOrientation() {
    let orientation = "horizontal";
    let styles = window.getComputedStyle(this.slider);
    if (styles.flexDirection.indexOf("column") > -1) {
      orientation = "vertical";
    }
    return orientation;
  }

  initPages() {
    let orientation = this.gerOrientation();
    // 1) Filter out zero-size slides (width for horizontal, height for vertical)
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) =>
      orientation === "vertical"
        ? element.clientHeight > 0
        : element.clientWidth > 0,
    );
    if (this.sliderItemsToShow.length < 2) return;

    // 2) Calculate the offset between first and second slide
    if (orientation === "vertical") {
      // Use offsetTop for vertical sliders
      this.sliderItemOffset =
        this.sliderItemsToShow[1].offsetTop -
        this.sliderItemsToShow[0].offsetTop;

      // Calculate how many slides fit vertically
      this.slidesPerPage = Math.floor(
        (this.slider.clientHeight - this.sliderItemsToShow[0].offsetTop) /
          this.sliderItemOffset,
      );
    } else {
      // Default horizontal calculation
      this.sliderItemOffset =
        this.sliderItemsToShow[1].offsetLeft -
        this.sliderItemsToShow[0].offsetLeft;
      this.slidesPerPage = Math.floor(
        (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
          this.sliderItemOffset,
      );
    }

    // 3) Determine total number of “pages”
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;

    // 4) Call update() to recalc which buttons should be enabled/disabled, etc.
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    if (this.enableSliderLooping) return;

    // Check the first slide to decide if "prev" should be disabled

    let orientation = this.gerOrientation();
    if (
      orientation === "vertical"
        ? this.isSlideVisible(this.sliderItemsToShow[0]) &&
          this.slider.scrollTop === 0
        : this.isSlideVisible(this.sliderItemsToShow[0]) &&
          this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    // Check the last slide to decide if "next" should be disabled
    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1],
      )
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    let orientation = this.gerOrientation();
    // If orientation is vertical, measure top offset, otherwise measure left offset
    if (orientation === "vertical") {
      const lastVisibleSlide =
        this.slider.clientHeight + this.slider.scrollTop - offset;
      return (
        element.offsetTop + element.clientHeight <= lastVisibleSlide &&
        element.offsetTop >= this.slider.scrollTop
      );
    } else {
      // Default horizontal check
      const lastVisibleSlide =
        this.slider.clientWidth + this.slider.scrollLeft - offset;
      return (
        element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
        element.offsetLeft >= this.slider.scrollLeft
      );
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = parseInt(event.currentTarget.dataset.step || 1);

    let orientation = this.gerOrientation();

    if (orientation === "vertical") {
      this.slideScrollPosition =
        event.currentTarget.name === "next"
          ? this.slider.scrollTop + step * this.sliderItemOffset
          : this.slider.scrollTop - step * this.sliderItemOffset;

      this.setSlidePosition(this.slideScrollPosition, "vertical");
    } else {
      this.slideScrollPosition =
        event.currentTarget.name === "next"
          ? this.slider.scrollLeft + step * this.sliderItemOffset
          : this.slider.scrollLeft - step * this.sliderItemOffset;

      this.setSlidePosition(this.slideScrollPosition, "horizontal");
    }
  }

  setSlidePosition(position, orientation = "horizontal") {
    if (orientation === "vertical") {
      this.slider.scrollTo({
        top: position,
        behavior: "smooth",
      });
    } else {
      this.slider.scrollTo({
        left: position,
        behavior: "smooth",
      });
    }
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link"),
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this)),
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    this.prevSlideButton = this.querySelector('button[name="previous-slide"]');
    this.nextSlideButton = this.querySelector('button[name="next-slide"]');

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.prevSlideButton) {
      this.prevSlideButton.addEventListener(
        "click",
        this.changeSlide.bind(this),
      );
    }

    if (this.nextSlideButton) {
      this.nextSlideButton.addEventListener(
        "click",
        this.changeSlide.bind(this),
      );
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  changeSlide(event) {
    if (event.currentTarget.name === "previous-slide") {
      const activeSlide = this.slider.querySelector("li.is-active");
      const previousSlide =
        activeSlide.previousElementSibling ||
        this.sliderItems[this.sliderItems.length - 1];

      activeSlide.classList.remove("is-active");
      previousSlide.classList.add("is-active");
    } else if (event.currentTarget.name === "next-slide") {
      const activeSlide = this.slider.querySelector("li.is-active");
      const nextSlide = activeSlide.nextElementSibling || this.sliderItems[0];

      activeSlide.classList.remove("is-active");
      nextSlide.classList.add("is-active");
    }
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this),
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    let page = 1;
    if (this.currentPage) {
      page = this.currentPage - 1;
    }
    this.sliderControlButtons[page].classList.add(
      "slider-counter__link--active",
    );
    this.sliderControlButtons[page].setAttribute("aria-current", true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed,
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow,
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow,
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class LensesInventoryStatus extends HTMLElement {
  constructor() {
    super();
  }

  /**
   * @param {Object} status
   * @param {boolean} status.isOutOfStock - true if no inventory
   * @param {boolean} status.isLowStock - true if leftover inventory <= threshold
   * @param {number}  status.leftover - how many "packs" we can fulfill
   */
  updateStatus(status = {}) {
    if (!status) return;

    // If out of stock => hide the element
    if (status.isOutOfStock) {
      this.style.display = "none";
      this.innerHTML = "";
      return;
    }

    // Otherwise we are in stock => show the element
    this.style.display = "flex";

    // Prepare text from window.variantStrings
    const { inStock, lowInStock, lowInStockSub, inStockSub } =
      window.variantStrings || {};

    let mainMessage = "";
    let subMessage = "";

    // If isLowStock, show the "low in stock" message
    if (status.isLowStock && status.leftover >= 1) {
      mainMessage = lowInStock?.replace("[value]", status.leftover) || "";
      subMessage = lowInStockSub || "";
    } else {
      // Otherwise show the "in stock" message
      mainMessage = inStock || "In stock";
      subMessage = inStockSub || "";
    }

    // Build final HTML
    this.innerHTML = `
      <p class="product__inventory" role="status">
        <span class="product__inventory-message">
          <svg width="15" height="15" aria-hidden="true" focusable="false" class="icon icon-success" viewBox="0 0 13 13">         
            <path d="M6.5 12.35C9.73087 12.35 12.35 9.73086 12.35 6.5C12.35 3.26913 9.73087 0.65 6.5 0.65C3.26913 0.65 0.65 3.26913 0.65 6.5C0.65 9.73086 3.26913 12.35 6.5 12.35Z" fill="#22a973" stroke="white" stroke-width="0.7"></path>
            <path d="M5.53271 8.66357L9.25213 4.68197" stroke="white"></path>
            <path d="M4.10645 6.7688L6.13766 8.62553" stroke="white"></path>
          </svg>
          ${mainMessage}
        </span>
        <span class="product__inventory-submessage">${subMessage}</span>
      </p>
    `;
  }
}

customElements.define("lenses-inventory-status", LensesInventoryStatus);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue,
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset",
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset",
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked"),
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);
class MultiPackVariantSelect extends HTMLElement {
  constructor() {
    super();
    this.selectedPack = "multipack-1";
    this.addToCart = document.querySelector(
      `#ProductSubmitButton-${this.dataset.section}`,
    );
    this.updateSelectedForm();
    this.addEventListener("change", (event) => this.handleChange(event));
  }

  handleChange(event) {
    const target = event.target;
    if (target.name === "pack-size") {
      this.selectedPack = target.value;
      this.updateSubTotal(target);
      this.updateSelectedForm();
    }
  }

  updateSubTotal(target) {
    let subtotal = document.querySelector(
      "#multipack-subtotal-" + this.dataset.section,
    );
    let savings = document.querySelector(
      "#multipack-saving-" + this.dataset.section,
    );
    if (subtotal && target.dataset.price) {
      subtotal.innerHTML = target.dataset.price;
    }
    if (savings && target.dataset.savings) {
      savings.innerHTML = target.dataset.savings;
      savings.parentNode.style.display = "block";
      if (parseInt(target.dataset.savings) === 0) {
        savings.parentNode.style.display = "none";
      }
    }
  }

  updateSelectedForm() {
    if (this.selectedPack && this.addToCart) {
      this.addToCart.setAttribute("form", this.selectedPack + "-form");
    }
    this.logSelectedPack();
  }

  logSelectedPack() {
    // console.log(`Selected Pack: ${this.selectedPack}`);
  }
}
customElements.define("multipack-variant-select", MultiPackVariantSelect);

class LensesVariantSelects extends MultiPackVariantSelect {
  constructor() {
    super();
    this.variantsMap = this.getUpdatedVariantsMap();
    this.supportedEyePowers = [
      "0.00",
      "-0.50",
      "-0.75",
      "-1.00",
      "-1.25",
      "-1.50",
      "-1.75",
      "-2.00",
      "-2.25",
      "-2.50",
      "-2.75",
      "-3.00",
      "-3.25",
      "-3.50",
      "-3.75",
      "-4.00",
      "-4.25",
      "-4.50",
      "-4.75",
      "-5.00",
      "-5.25",
      "-5.50",
      "-5.75",
      "-6.00",
    ];
    let eyePower = this.getValidEyePower(
      getLocalStorageItem("p"),
      this.dataset.selectedEyePower || "0.00",
    );
    this.saveEyePower(eyePower);
    this.addEventListener("change", (event) => this.handleSelectChange(event));
  }

  getUpdatedVariantsMap() {
    let node = this.querySelector(
      'script[type="application/json"][name="variants"]',
    );
    if (node && node.textContent) {
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        console.error("Failed to parse variant id map", e);
      }
    }
    return {};
  }

  getValidEyePower(selectedEyePower, defaultValue) {
    if (selectedEyePower && selectedEyePower.length) {
      let eyePowers = selectedEyePower.split(",");
      let leftEye, rightEye;
      if (eyePowers[0] && this.supportedEyePowers.indexOf(eyePowers[0]) >= 0) {
        leftEye = eyePowers[0];
      }
      if (eyePowers[1] && this.supportedEyePowers.indexOf(eyePowers[1]) >= 0) {
        rightEye = eyePowers[1];
      }
      let validPower = [leftEye, rightEye].filter((o) => !!o).join(",");
      if (validPower && validPower.length) return validPower;
    }
    return defaultValue || "0.00";
  }

  connectedCallback() {
    this.render();
  }

  handleSelectChange(event) {
    const target = event.target;
    if (target.name === "different-eye-powers") {
      this.toggleDifferentEyePowers(target.checked);
    } else if (target.name === "eye-power-option") {
      this.saveEyePower(target.value);
    } else if (
      target.name === "left-eye-power" ||
      target.name === "right-eye-power"
    ) {
      this.updateDualEyePower();
    } else if (target.name === "pack-size") {
      this.selectedPack = target.value;
      this.updatePackOptions();
      this.updateFormContent();
    }
  }

  toggleDifferentEyePowers(isChecked) {
    const singleEyePower = this.querySelector(".single-eye-power");
    const differentEyePowerDetails = this.querySelector(
      ".different-eye-power-details",
    );
    if (singleEyePower && differentEyePowerDetails) {
      if (isChecked) {
        singleEyePower.style.display = "none";
        differentEyePowerDetails.style.display = "grid";
        // if not multi power in stored value then update to two eye powers
        if (!this.eyePower.includes(",")) {
          this.querySelector('select[name="left-eye-power"]').value =
            this.eyePower;
          this.querySelector('select[name="right-eye-power"]').value =
            this.eyePower;
          this.saveEyePower(`${this.eyePower},${this.eyePower}`);
        }
      } else {
        singleEyePower.style.display = "grid";
        differentEyePowerDetails.style.display = "none";
        // if  multi power in stored value then update to single eye powers
        if (this.eyePower.includes(",")) {
          let [singlePower] = this.eyePower.split(",");
          this.querySelector('select[name="eye-power-option"]').value =
            singlePower;
          this.saveEyePower(singlePower);
        }
      }
      this.updatePackOptions();
    }
  }

  updateDualEyePower() {
    const differentEyePowersChecked = this.querySelector(
      'input[name="different-eye-powers"]',
    ).checked;
    if (differentEyePowersChecked) {
      const leftEyePower = this.querySelector(
        'select[name="left-eye-power"]',
      ).value;
      const rightEyePower = this.querySelector(
        'select[name="right-eye-power"]',
      ).value;
      this.saveEyePower(`${leftEyePower},${rightEyePower}`);
    }
  }

  updatePackOptions() {
    const onePackOption = this.querySelector(
      'input[name="pack-size"][value="multipack-1"]',
    );
    const differentEyePowersChecked = this.querySelector(
      'input[name="different-eye-powers"]',
    ).checked;
    if (onePackOption) {
      if (differentEyePowersChecked) {
        onePackOption.disabled = true;
        const twoPackOption = this.querySelector(
          'input[name="pack-size"][value="multipack-2"]',
        );
        if (this.selectedPack === "multipack-1") {
          this.selectedPack = "multipack-2";
          if (twoPackOption) {
            twoPackOption.checked = true;
            this.updateSubTotal(twoPackOption);
          }
        }
      } else {
        onePackOption.disabled = false;
      }
    }
    this.updateSelectedForm();
  }

  updateFormContent() {
    let [leftEyePower, rightEyePower] = this.eyePower.split(",");
    if (!leftEyePower) {
      leftEyePower = "0.00";
    }
    if (!rightEyePower) {
      rightEyePower = leftEyePower;
    }

    // First let's update the single pack form content
    let multipackOneForm = this.querySelector("form#multipack-1-form");
    if (multipackOneForm) {
      let variantInput = multipackOneForm.querySelector('input[name="id"]');
      if (
        variantInput &&
        this.variantsMap[leftEyePower] &&
        this.variantsMap[leftEyePower].variantId
      ) {
        variantInput.value = this.variantsMap[leftEyePower].variantId;
      }
    }

    // Now let's update the two pack form content
    let multipackTwoForm = this.querySelector("form#multipack-2-form");
    if (multipackTwoForm) {
      let leftEyeInput = multipackTwoForm.querySelector(
        'input[name="properties[_left-eye]"]',
      );
      let rightEyeInput = multipackTwoForm.querySelector(
        'input[name="properties[_right-eye]"]',
      );
      let bundleSelectionInput = multipackTwoForm.querySelector(
        'input[name="properties[_bundle_selection]"]',
      );
      if (leftEyeInput && rightEyeInput && bundleSelectionInput) {
        leftEyeInput.value = leftEyePower;
        rightEyeInput.value = rightEyePower;
        bundleSelectionInput.value = `${leftEyePower} <> ${rightEyePower}`;
      }
    }

    // Now let's update the fourth pack form content
    let multipackThirdForm = this.querySelector("form#multipack-3-form");
    if (multipackThirdForm) {
      let leftEyeInput = multipackThirdForm.querySelector(
        'input[name="properties[_left-eye]"]',
      );
      let rightEyeInput = multipackThirdForm.querySelector(
        'input[name="properties[_right-eye]"]',
      );
      let bundleSelectionInput = multipackThirdForm.querySelector(
        'input[name="properties[_bundle_selection]"]',
      );
      if (leftEyeInput && rightEyeInput && bundleSelectionInput) {
        leftEyeInput.value = leftEyePower;
        rightEyeInput.value = rightEyePower;
        bundleSelectionInput.value = `${leftEyePower} <> ${rightEyePower}`;
      }
    }

    // Now let's check the inventory status
    if (
      this.variantsMap &&
      this.variantsMap[leftEyePower] &&
      this.variantsMap[rightEyePower] &&
      this.addToCart &&
      this.addToCart.form
    ) {
      let selectedForm = this.addToCart.getAttribute("form");
      let leftEyeInventory = this.variantsMap[leftEyePower]["inventory"];
      let rightEyeInventory = this.variantsMap[rightEyePower]["inventory"];
      // We'll define leftover = how many *packs* we can fulfill
      let leftover = 0;

      let outOfStock = false;
      if (selectedForm === "multipack-1-form") {
        // 1-pack => need 1 lens in the chosen power
        // We'll consider only the "left power" for the single pack example
        leftover = leftEyeInventory;
        if (leftover < 1) outOfStock = true;
      } else if (selectedForm === "multipack-2-form") {
        // 2-pack => need 2 lenses total
        if (leftEyePower !== rightEyePower) {
          // If different eye powers, need 1 lens from left and 1 from right
          // leftover is how many "pairs" we can form
          leftover = Math.min(leftEyeInventory, rightEyeInventory);
          if (leftover < 1) outOfStock = true;
        } else {
          // If same eye power, we need 2 from that inventory
          leftover = Math.floor(leftEyeInventory / 2);
          if (leftover < 1) outOfStock = true;
        }
      } else if (selectedForm === "multipack-3-form") {
        // 4-pack => need 4 lenses total
        // (in your code it's called "multipack-3-form", but let's assume it means 4 lenses)
        if (leftEyePower !== rightEyePower) {
          // If different eye powers => need 2 from left and 2 from right
          leftover = Math.min(
            Math.floor(leftEyeInventory / 2),
            Math.floor(rightEyeInventory / 2),
          );
          if (leftover < 1) outOfStock = true;
        } else {
          // If same eye power => need 4 from that single inventory
          leftover = Math.floor(leftEyeInventory / 4);
          if (leftover < 1) outOfStock = true;
        }
      }

      // (3) Mark whether it’s “low in stock”
      // (for instance, leftover <= 10 means low in stock)
      let isLowStock = false;
      if (!outOfStock && leftover > 0 && leftover < 10) {
        isLowStock = true;
      }

      let submitButtonText = this.addToCart.querySelector("span");
      let notifyButton = document.querySelector(
        "#NotifyMeButton-" + this.dataset.section,
      );
      if (outOfStock) {
        this.addToCart.setAttribute("disabled", "disabled");
        this.addToCart.classList.remove("button--primary");
        this.addToCart.classList.remove("button--3d");
        this.addToCart.classList.remove("color-scheme-success");
        this.addToCart.classList.remove("gradient");
        this.addToCart.classList.add("button--secondary");
        if (submitButtonText) {
          submitButtonText.textContent = window.variantStrings.soldOut;
        }
        if (notifyButton) {
          notifyButton.style.display = "block";
        }
      } else {
        this.addToCart.removeAttribute("disabled");
        this.addToCart.classList.remove("button--secondary");
        this.addToCart.classList.add("button--primary");
        this.addToCart.classList.add("button--3d");
        this.addToCart.classList.add("color-scheme-success");
        this.addToCart.classList.add("gradient");
        if (submitButtonText) {
          submitButtonText.textContent = window.variantStrings.addToCart;
        }
        if (notifyButton) {
          notifyButton.style.display = "none";
        }
      }

      const inventoryStatusEl = document.querySelector(
        "lenses-inventory-status",
      );
      if (inventoryStatusEl) {
        inventoryStatusEl.updateStatus({
          isOutOfStock: outOfStock,
          isLowStock: isLowStock,
          leftover: leftover,
        });
      }
    }
  }

  saveEyePower(eyePower) {
    this.eyePower = this.getValidEyePower(eyePower);
    setLocalStorageItem("p", this.eyePower);
    this.updateFormContent();
    // console.log(`Selected Eye Power: ${this.eyePower}`);
  }

  render() {
    this.setEyePower();
  }

  setEyePower() {
    const isMultiPower = this.eyePower.includes(",");

    if (!isMultiPower) {
      this.querySelector('select[name="eye-power-option"]').value =
        this.eyePower;
    } else {
      const [leftEyePower, rightEyePower] = this.eyePower.split(",");
      this.querySelector('input[name="different-eye-powers"]').checked = true;
      this.toggleDifferentEyePowers(true);
      this.querySelector('select[name="left-eye-power"]').value = leftEyePower;
      this.querySelector('select[name="right-eye-power"]').value =
        rightEyePower;
    }
    this.updatePackOptions();
  }
}

customElements.define("lenses-variant-selects", LensesVariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" },
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`,
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this),
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement),
    );
    const quickBulkElement =
      this.closest("quick-order-list") || this.closest("quick-add-bulk");
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min,
        ),
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max,
        ),
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step,
        ),
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

class GlobalEShippingBanner extends HTMLElement {
  constructor() {
    super();
    this.parentContainer =
      document.querySelector(this.dataset.parentContainer) ||
      this.parentElement;
    // if (this.parentContainer) this.parentContainer.style.display = "none";

    // Check if Global-E is already initialized.
    if (
      document.querySelector("body").getAttribute("ge-active") &&
      document.querySelector("body").getAttribute("ge-active") === "true"
    )
      return this.initCallback();

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.addedNodes.length &&
          [].some.call(
            mutation.addedNodes,
            (node) =>
              node.classList &&
              node.classList.contains("ge-free-shipping-container"),
          )
        ) {
          this.observer.disconnect();
          this.initCallback();
        }
      });
    });
  }

  initCallback() {
    typeof this.globalEInit === "function" && this.globalEInit();
  }

  connectedCallback() {
    this.observer &&
      this.observer.observe(document.body, {
        childList: true,
      });
  }

  parseGlobalEShippingCost(text) {
    try {
      if (text.startsWith("@")) {
        return null;
      } else if (text.endsWith("on all orders")) {
        return 0;
      }
      const value = text.split(" ").reverse()[0];

      // Remove non-digit characters except ',' and '.'
      const step1 = value.replaceAll(/[^0-9,.]/g, "");

      // Remove '.' occurring at the beginning of the string
      const parsedValue = step1.startsWith(".") ? step1.substring(1) : step1;

      // Parse the value as a fraction
      const parsedFloat = parseFloat(parsedValue.replaceAll(",", ""));

      // Assign the parsed value to the corresponding key in the new object
      if (!isNaN(parsedFloat)) {
        return parsedFloat;
      }
    } catch {
      console.error("Failed to parse Global-E Shipping Bar value: " + text);
    }
    return null;
  }

  globalEInit() {
    const freeShippingBanner = document.querySelector("#FreeShippingBanner");

    // Set the innerHTML of the header-promo element to the textContent
    if (freeShippingBanner) {
      let parseNo = this.parseGlobalEShippingCost(
        freeShippingBanner.textContent,
      );
      if (parseNo !== null) {
        this.render(parseNo);
      }
    }
  }

  render(shippingValue) {
    if (!(this.dataset.shippingText || this.dataset.freeShippingText)) return;
    let text =
      shippingValue === 0
        ? this.dataset["freeShippingText"]
        : this.dataset["shippingText"];
    this.innerHTML = text
      .replace("[country]", this.dataset.country)
      .replace(
        "[value]",
        `<strong class="no-break">${Shopify.formatMoney(
          shippingValue * 100,
        )}</strong>`,
      );
    this.parentContainer.style.display = "block";
  }
}

if (!customElements.get("global-e-shipping")) {
  customElements.define("global-e-shipping", GlobalEShippingBanner);
}

class ShippingBar extends GlobalEShippingBanner {
  constructor() {
    super();
    if (
      window &&
      window.Shopify &&
      window.Shopify.shop === "swati-india.myshopify.com"
    ) {
      this.render(0); // We offer free shipping in India
    }
  }
  render(shippingValue) {
    let textNode = this.querySelector(".unlock-shipping-title");
    if (
      !(
        textNode &&
        textNode.dataset.template &&
        textNode.dataset.unlocked &&
        textNode.dataset.cartTotal
      )
    )
      return;
    let remaining = shippingValue * 100 - parseInt(textNode.dataset.cartTotal);
    let remainingPercent = 100 - Math.round(remaining / shippingValue);
    if (remaining <= 0 || shippingValue === 0) {
      textNode.innerHTML = textNode.dataset["unlocked"];
      textNode.parentElement.classList.add("success");
      remainingPercent = 100;
    } else {
      textNode.innerHTML = textNode.dataset["template"].replace(
        "[remaining_amount]",
        `${Shopify.formatMoney(remaining)}`,
      );
    }
    let bar = this.querySelector(".progress-bar");
    if (bar) {
      bar.style.width = `${remainingPercent}%`;
    }
    textNode.parentElement.classList.remove("loading");
    textNode.parentElement.style.display = "block";
  }
}

if (!customElements.get("shipping-bar")) {
  customElements.define("shipping-bar", ShippingBar);
}

class FreeShippingValue extends GlobalEShippingBanner {
  constructor() {
    super();
  }

  render(shippingValue) {
    this.innerHTML = `${Shopify.formatMoney(shippingValue * 100)}`;
  }
}

if (!customElements.get("free-shipping-value")) {
  customElements.define("free-shipping-value", FreeShippingValue);
}

class CampaignTimer extends HTMLElement {
  constructor() {
    super();

    if (this.dataset.repeat) {
      let today = new Date();
      let nextDay = new Date(today);
      nextDay.setDate(today.getDate() + 2);

      let dd = String(nextDay.getDate()).padStart(2, "0");
      let mm = String(nextDay.getMonth() + 1).padStart(2, "0"); // January is 0
      let yyyy = nextDay.getFullYear();

      let timezone = "+01:00";
      if (
        window.Shopify &&
        window.Shopify.shop === "swati-india.myshopify.com"
      ) {
        timezone = "+05:30";
      }
      let nextDayFormatted = `${yyyy}-${mm}-${dd}T00:00:00${timezone}`;
      this.endDate = new Date(nextDayFormatted).getTime();
      this.hideDays = true;
    } else {
      if (
        !window.promotional_timer ||
        !(window.promotional_timer && window.promotional_timer.date)
      )
        return;

      // Get the campaign end date time from the window object (it's in iso 8601 format)
      this.endDate = new Date(window.promotional_timer.date).getTime();
      this.hideDays = window.promotional_timer.hide_days;
    }
    this.init();
  }
  toTwoDigits(num) {
    return num < 10 ? "0" + num : num.toString();
  }

  getHTMLFormat(days, hours, minutes, seconds) {
    return `
    <div>
        ${
          !this.hideDays
            ? `<div><span>${this.toTwoDigits(
                days,
              )}</span><strong>Day</strong></div><span>:</span>`
            : ""
        }
        <div><span>${this.toTwoDigits(
          hours,
        )}</span><strong>Hours</strong></div><span>:</span>
       <div><span>${this.toTwoDigits(
         minutes,
       )}</span><strong>Min</strong></div><span>:</span>
        <div><span>${this.toTwoDigits(seconds)}</span><strong>Sec</strong></div>
    </div>
    `;
  }

  getFormat(days, hours, minutes, seconds) {
    return this.hideDays
      ? `${hours}h ${minutes}m ${seconds}s`
      : `${
          days && !isNaN(days) ? `${days}d` : ""
        } ${hours}h ${minutes}m ${seconds}s`;
  }

  init() {
    // get the distance between now and the end of the campaign
    const getDistance = () => this.endDate - new Date().getTime();

    if (this.endDate && getDistance() > 0) {
      const x = setInterval(() => {
        const distance = getDistance();
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        );
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        this.innerHTML =
          this.dataset.template === "html"
            ? this.getHTMLFormat(days, hours, minutes, seconds)
            : this.getFormat(days, hours, minutes, seconds);

        if (getDistance() < 0) {
          clearInterval(x);
          this.innerHTML = "";
          localStorage.removeItem("fbPromoCode");
          localStorage.removeItem("fbPromoEndTime");
        }
      }, 1000);
    }
  }
}

customElements.define("campaign-timer", CampaignTimer);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      },
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = "";

    if (inputValue > 0) {
      if (inputValue < event.target.dataset.min) {
        message = window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min,
        );
      } else if (inputValue > parseInt(event.target.max)) {
        message = window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max,
        );
      } else if (inputValue % parseInt(event.target.step) !== 0) {
        message = window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step,
        );
      }
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute("name"),
        event.target.dataset.quantityVariantId,
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html",
          );
          const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html",
          );
          const sourceQty = html.querySelector("cart-items");
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "unlock-shipping-cart-section",
        section: document.getElementById("unlock-shipping-cart-section").dataset
          .id,
        selector: ".js-contents",
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");

        if (cartFooter)
          cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0,
          );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector,
          );
        });
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = "";
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue,
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`),
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector(".drawer__inner-empty"),
            cartDrawerWrapper.querySelector("a"),
          );
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item__name"),
          );
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch(() => {
        this.querySelectorAll(".loading__spinner").forEach((overlay) =>
          overlay.classList.add("hidden"),
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").textContent =
        message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`,
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`,
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden"),
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`,
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`,
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden"),
    );
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "input",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this),
    );
    this.setHeaderCartIconAccessibility();
    window.addEventListener("update_cart", this.refreshCart.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (window.disableCartDrawer) {
      window.location.href =
        window.routes && window.routes.cart_url
          ? window.routes.cart_url
          : "/cart";
      return;
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id,
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open"),
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderCartContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    if (parsedState.id) {
      this.productId = parsedState.id;
    }
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this),
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: window["cart_section_id"] || "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCart() {
    if (!!window.refreshingCart) return;
    window.refreshingCart = true;
    return fetch(
      `${routes.cart_url}?sections=${this.getSectionsToRender()
        .map((o) => o.id)
        .join()}`,
    )
      .then((response) => response.json())
      .then((response) => {
        this.renderCartContents({ sections: response });
      })
      .finally(() => {
        window.refreshingCart = false;
      });
  }
  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: window["cart_section_id"] || "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

document.addEventListener("DOMContentLoaded", function () {
  function applyZIndexToFormEmbeds() {
    const formEmbeds = document.querySelectorAll("form-embed");

    formEmbeds.forEach(function (formEmbed) {
      const shadowRoot = formEmbed.shadowRoot;

      if (shadowRoot) {
        const appEmbed = shadowRoot.getElementById("app-embed");

        if (appEmbed) {
          const style = document.createElement("style");
          style.textContent =
            "#app-embed { z-index: 2 !important; } button[aria-controls=country-selector-dropdown] { max-height: 54px; } form { text-align: left; }";
          shadowRoot.appendChild(style);
        }
      }
    });
  }

  // Initial application in case the elements are already in the DOM
  applyZIndexToFormEmbeds();

  // Observer to watch for new form-embed elements being added
  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.type === "childList") {
        mutation.addedNodes.forEach(function (node) {
          if (node.tagName === "FORM-EMBED") {
            applyZIndexToFormEmbeds();
          }
        });
      }
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
});
